import React, { useState, useEffect, memo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Dashboard,
  AutoAwesome,
  History,
  AccountBalance,
  Person,
  Settings,
  Logout,
  Notifications,
  Add,
  AutoAwesome as SparklesIcon,
  CreditCard,
  Close,
  CheckCircle,
  Assignment,
  Info,
  Language
} from '@mui/icons-material';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { creditsAPI } from '../../../services/merchantAPI';
import { formatCredits } from '../../../utils/numberFormat';
import FlutterServicesAPI from '../../../services/flutterServices';
import VerificationStatusBanner from '../Common/VerificationStatusBanner';
import { masterDataConfigAPI } from '../../../services/api';
import merchantAPI from '../../../services/merchantAPI'; // Import merchantAPI for profile calls
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import UsageLimitExceededModal from '../../Common/UsageLimitExceededModal';
import WhatsAppRequiredModal from '../../Common/WhatsAppRequiredModal';


// Memoized Credit Balance Component - only re-renders when credits actually change
const CreditBalance = memo(({ credits, isUpdating }) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
    <Typography
      variant="h6"
      sx={{
        color: '#6318C6',
        fontWeight: 700,
        fontSize: { xs: 18, md: 22 },
        lineHeight: 1
      }}
    >
      {credits !== undefined ? formatCredits(credits) : '0'}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: '#374151',
        fontWeight: 500,
        fontSize: { xs: 16, md: 18 }
      }}
    >
      {FlutterServicesAPI.getCreditLabel()}
    </Typography>
    {isUpdating && (
      <CircularProgress size={20} sx={{ color: 'rgba(0,0,0,0.6)' }} />
    )}
  </Box>
), (prevProps, nextProps) => {
  // Only re-render if credits balance or updating status actually changes
  return prevProps.credits === nextProps.credits && prevProps.isUpdating === nextProps.isUpdating;
});

// Memoized Mobile Credit Balance Component
const MobileCreditBalance = memo(({ credits, isUpdating }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="body1" sx={{ color: 'black', fontWeight: 500, fontSize: 14 }}>
      {FlutterServicesAPI.getCreditLabel()}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="h4" sx={{ color: '#6318C6', fontWeight: 700, lineHeight: 1.1, fontSize: { xs: 22, sm: 24, md: 28 } }}>
        {credits !== undefined ? formatCredits(credits) : '0'}
      </Typography>
      {isUpdating && (
        <CircularProgress size={20} sx={{ color: 'rgba(0,0,0,0.6)' }} />
      )}
    </Box>
  </Box>
), (prevProps, nextProps) => {
  // Only re-render if credits balance or updating status actually changes
  return prevProps.credits === nextProps.credits && prevProps.isUpdating === nextProps.isUpdating;
});

const MerchantLayout = memo(({ children }) => {
  const location = useLocation();
  const isCreatePage = location.pathname === '/generate';
  const { merchant, logout, refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const canViewProfile = hasPermission('Merchant Profile', 'view');
  const canViewAIModelStudio = hasPermission('AI Model Studio', 'view');
  const canViewBrands = hasPermission('Brand Management', 'view');
  const canRequestTokens = hasPermission('Credits Management', 'request_tokens');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  // Check auth visibility on mount
  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ MerchantLayout: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  // Console log merchant profile info when merchant data changes
  useEffect(() => {
    if (merchant) {
      // console.log('👤 Merchant Profile Info:', {
      //   id: merchant.id,
      //   name: merchant.name,
      //   email: merchant.email,
      //   credits_balance: merchant.credits_balance,
      //   canViewProfile: canViewProfile,
      //   profileMenuOpen: Boolean(profileMenuAnchor),
      //   currentPath: location.pathname
      // });
    } else {
      // console.log('👤 No merchant data available');
    }
  }, [merchant, canViewProfile, profileMenuAnchor, location.pathname]);

  // Refresh merchant context when component mounts or route changes to keep credit balance updated
  useEffect(() => {
    console.log('🔄 MerchantLayout: Refreshing merchant context on route change to:', location.pathname);
    refreshProfile();
  }, [location.pathname]);

  // Initial refresh when component mounts
  useEffect(() => {
    console.log('🔄 MerchantLayout: Initial refresh on component mount');
    refreshProfile();
  }, []);

  // Additional refresh when component mounts to ensure credit balance is current
  // COMMENTED OUT: Unnecessary API call that may impact performance
  // Uncomment if you need this additional refresh functionality
  /*
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔄 MerchantLayout: Additional refresh after 1 second');
      refreshProfile();
    }, 1000); // Small delay to ensure other data is loaded first
    
    return () => clearTimeout(timer);
  }, []);
  */

  // Refresh merchant context when page becomes visible (e.g., after refresh)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh merchant context
        console.log('🔄 MerchantLayout: Page became visible, refreshing merchant context');
        refreshProfile();
      }
    };

    const handleFocus = () => {
      // Page gained focus, refresh merchant context
      console.log('🔄 MerchantLayout: Page gained focus, refreshing merchant context');
      refreshProfile();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);



  // Credit dialog states
  const [customCreditsDialog, setCustomCreditsDialog] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [maxCreditLimit, setMaxCreditLimit] = useState(10000000); // Default fallback for MAX_CREDIT_ALLOCATION_LIMIT
  const [validationError, setValidationError] = useState('');
  const [error, setError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  const [isUpdatingCredits, setIsUpdatingCredits] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(null);

  useEffect(() => {
    const handler = (e) => setLimitExceeded(e.detail);
    window.addEventListener('usage_limit_exceeded', handler);
    return () => window.removeEventListener('usage_limit_exceeded', handler);
  }, []);


  /**
   * Handle complete logout process including FCM token deactivation for mobile apps
   * This ensures that when a user logs out from a mobile device, their FCM token
   * is properly deactivated so they won't receive push notifications
   */
  const handleLogout = async () => {
    try {
      console.log('🚪 Starting logout process...');

      // First, logout FCM token if user is on mobile app
      if (FlutterServicesAPI.isFlutterApp()) {
        console.log('📱 Mobile app detected, logging out FCM token...');
        try {
          // Get current FCM token from device info if available
          const deviceInfo = await FlutterServicesAPI.getDeviceInfo();
          if (deviceInfo && deviceInfo.fcmToken) {
            console.log('🔥 Found FCM token, logging it out...');
            await FlutterServicesAPI.logoutFCMToken({
              fcm_token: deviceInfo.fcmToken
            });
            console.log('✅ FCM token logged out successfully');
          } else {
            console.log('ℹ️ No FCM token found, skipping FCM logout');
          }
        } catch (fcmError) {
          console.warn('⚠️ FCM token logout failed:', fcmError);
          // Continue with logout even if FCM logout fails
        }
      } else {
        console.log('🌐 Web environment, skipping FCM token logout');
      }

      // Then proceed with regular logout
      console.log('🔐 Proceeding with regular logout...');
      await logout();
      console.log('✅ Logout completed successfully');
      navigate('/login');
    } catch (error) {
      console.error('❌ Logout failed:', error);
      // Still try to navigate to login even if logout fails
      navigate('/login');
    }
  };

  const handleProfileMenuOpen = (event) => {
    console.log('👤 Profile menu opened', {
      merchant: merchant ? {
        id: merchant.id,
        name: merchant.name,
        email: merchant.email
      } : null,
      canViewProfile: canViewProfile
    });
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    console.log('👤 Profile menu closed');
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    console.log('👤 Profile clicked', {
      canViewProfile: canViewProfile,
      merchant: merchant ? {
        id: merchant.id,
        name: merchant.name,
        email: merchant.email
      } : null
    });

    if (!canViewProfile) {
      // console.log('❌ Profile access denied - insufficient permissions');
      return;
    }

    // console.log('✅ Navigating to profile page');
    navigate('/profile');
    handleProfileMenuClose();
  };

  /**
   * Handle logout button click - closes profile menu and initiates logout process
   */
  const handleLogoutClick = async () => {
    handleProfileMenuClose(); // Close the profile menu first
    await handleLogout(); // Then handle the logout process
  };

  // Load system settings for credit limits on component mount
  // This fetches the MAX_CREDIT_ALLOCATION_LIMIT from the backend for validation
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        // Load system settings for credit limits
        const response = await masterDataConfigAPI.getPublicSystemSettings();
        const settings = response.data.settings;
        if (settings.MAX_CREDIT_ALLOCATION_LIMIT) {
          setMaxCreditLimit(settings.MAX_CREDIT_ALLOCATION_LIMIT);
        }
      } catch (error) {
        console.error('Failed to fetch system settings:', error);
        // Keep default value if fetch fails
      }
    };

    // Load merchant profile data on component mount
    const loadMerchantProfile = async () => {
      try {
        if (merchant) {
          // console.log('👤 Loading merchant profile data for:', {
          //   merchantId: merchant.id,
          //   merchantName: merchant.name,
          //   merchantEmail: merchant.email
          // });

          const profileResponse = await merchantAPI.getProfile();

          // Update merchant data if profile has changed
          if (profileResponse.data.merchant) {
            // console.log('✅ Merchant profile loaded successfully:', {
            //   profileData: profileResponse.data.merchant,
            //   creditsBalance: profileResponse.data.merchant.credits_balance,
            //   profileId: profileResponse.data.merchant.id
            // });
            // You can add logic here to update the merchant context if needed
          } else {
            // console.log('⚠️ No merchant data in profile response');
          }
        } else {
          // console.log('👤 No merchant data available for profile loading');
        }
      } catch (error) {
        // console.error('❌ Failed to load merchant profile:', {
        //   error: error.message,
        //   merchant: merchant ? {
        //     id: merchant.id,
        //     name: merchant.name,
        //     email: merchant.email
        //   } : null
        // });
        // Handle profile loading error gracefully
      }
    };

    // Execute both functions
    loadSystemSettings();
    loadMerchantProfile();
  }, [merchant]); // Add merchant as dependency to reload when merchant changes



  // Credit management functions
  // Validation function for credit amount - ensures input meets minimum and maximum requirements
  const validateCreditAmount = (amount) => {
    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < 100) {
      return `Minimum custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount is 100`;
    }
    if (numAmount > maxCreditLimit) {
      return `Maximum allowed is ${maxCreditLimit.toLocaleString()} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}`;
    }
    return null;
  };

  const handleCustomCredits = async () => {
    const validationError = validateCreditAmount(customAmount);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setIsUpdatingCredits(true);
      const response = await creditsAPI.requestCustomCredits({
        amount: parseInt(customAmount),
        notes: customNotes
      });

      setCustomCreditsDialog(false);
      setCustomAmount('');
      setCustomNotes('');
      setValidationError('');

      // Show beautiful success dialog
      setSuccessMessage(`Custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request submitted successfully!`);
      setRequestDetails({
        requestId: response.data?.request_id,
        status: 'PENDING',
        credits: customAmount,
        packageName: `Custom ${FlutterServicesAPI.getCreditLabel()}`
      });
      setSuccessDialog(true);

      // Refresh profile to get updated credit balance
      console.log('🔄 MerchantLayout: Refreshing merchant context after custom credit request');
      await refreshProfile();

    } catch (error) {
      console.error('Custom credit request failed:', error);
      setError(`Failed to submit custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request. Please try again.`);
    } finally {
      setLoading(false);
      setIsUpdatingCredits(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog(false);
    setSuccessMessage('');
    setRequestDetails(null);
  };

  const handleCancelCustomCredits = () => {
    setCustomCreditsDialog(false);
    setCustomAmount('');
    setCustomNotes('');
    setError('');
    setValidationError('');
  };

  // Console log when merchant data is rendered
  useEffect(() => {
    if (merchant) {
      // console.log('👤 Rendering merchant profile in UI:', {
      //   hasName: !!merchant.name,
      //   hasEmail: !!merchant.email,
      //   creditsBalance: merchant.credits_balance,
      //   avatarInitial: merchant.name ? merchant.name[0].toUpperCase() : (merchant.email ? merchant.email[0].toUpperCase() : 'P'),
      //   profileMenuVisible: Boolean(profileMenuAnchor)
      // });
    }
  }, [merchant, profileMenuAnchor]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'rgba(247, 244, 250, 1)' }} className="merchant-layout" data-merchant-page="true">
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'white',
          color: 'black',
          boxShadow: 'none',
          borderBottom: '1px solid #eee',

          // Mobile only
          position: { xs: 'fixed', md: 'static' },
          top: { xs: 0, md: 'auto' },
          left: { xs: 0, md: 'auto' },
          height: { xs: '73px', md: 'auto' },
          zIndex: { xs: 999, md: 'auto' },
          px: { xs: '1px', md: 0 },
          py: { xs: '9px', md: 0 },
          width: { xs: '100%', md: 'auto' }
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64, md: 72 }, py: { xs: 0.5, sm: 1 } }}>
          <Box display="flex" alignItems="center"
            sx={{ cursor: 'pointer', maxWidth: { xs: '50%', sm: 'auto' } }}
            onClick={() => !FlutterServicesAPI.isFlutterApp() && navigate('/')}
          // onClick={() => {
          //   if (location.pathname === '/dashboard') {
          //     navigate('/');
          //   } else {
          //     navigate('/dashboard');
          //   }
          // }}
          >
            <Box
              className="logo-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
                overflow: 'hidden'
              }}
            >
              {/* Replace CreditCard icon with sparkle logo image */}
              <img
                src="/assets/img/glammify-logo.png"
                alt="Glammify Logo"
                sx={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: { xs: 60, sm: 60, md: 60 },
                  objectFit: 'contain',
                  mr: { xs: 0.5, sm: 1 }
                }}
              />
            </Box>
          </Box>



          {/* Right side - different for desktop vs mobile */}
          <Box display="flex" alignItems="center" gap={2}>
            {merchant && (
              <>
                {/* Desktop Layout - Credit Info + Profile icon */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                  {/* Website Link - COMMENTED OUT: Go to Website button removed from merchant home page */}
                  {false && !FlutterServicesAPI.isFlutterApp() && (
                    <Tooltip
                      title="Go to Website"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '15px',
                          },
                        },
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => window.location.href = '/'}
                        startIcon={<Language sx={{ color: '#6318C6', fontSize: 32 }} />}
                        sx={{
                          background: 'white',
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          border: '1px solid rgba(99, 24, 197, 0.15)',
                          textTransform: 'none',
                          minWidth: 45,
                          minHeight: 45,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          '& .MuiButton-startIcon': {
                            margin: 0,
                            marginRight: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          },
                          '&:hover': {
                            background: '#f9fafb',
                            borderColor: '#d1d5db',
                          },
                        }}
                      >
                      </Button>
                    </Tooltip>
                  )}

                  {/* Credit Balance - HIDDEN AS PER REQUEST
                    <Button
                      variant="outlined"
                      sx={{
                        background: 'white',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        border: '1px solid rgba(99, 24, 197, 0.15)',
                        textTransform: 'none',
                        minWidth: 120,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          background: '#f9fafb',
                          borderColor: '#d1d5db',
                        },
                      }}
                    >
                      <CreditBalance
                        credits={merchant?.credits_balance}
                        isUpdating={isUpdatingCredits}
                      />
                    </Button>
                  */}

                  {/* Request Credits Button — gated by plan purchase_tokens permission */}
                  {showWhatsAppSmsAuth && (permissionLoading || canRequestTokens) && (
                    <Button
                      variant="contained"
                      onClick={() => navigate('/credits?tab=0')}
                      sx={{
                        background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)',
                        color: 'white',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 600,
                        borderRadius: '10px',
                        py: { xs: 1, sm: 1.2 },
                        textTransform: 'none',
                        fontSize: '14px',
                        boxShadow: 'none',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      <Add sx={{ fontSize: 18, mr: 1 }} />
                      Request {FlutterServicesAPI.getCreditLabel()}
                    </Button>
                  )}

                  {/* Profile icon with dropdown */}

                  <IconButton
                    onClick={handleProfileMenuOpen}
                    size="large"
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#a78bfa', width: 40, height: 40 }}>
                      {merchant.name ? merchant.name[0].toUpperCase() : (merchant.email ? merchant.email[0].toUpperCase() : <Person />)}
                    </Avatar>
                  </IconButton>

                </Box>



                {/* Mobile/Tablet Layout - Profile icon only */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    size="large"
                    sx={{
                      paddingRight: 0,
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#a78bfa', width: 40, height: 40 }}>
                      {merchant.name ? merchant.name[0].toUpperCase() : (merchant.email ? merchant.email[0].toUpperCase() : <Person />)}
                    </Avatar>
                  </IconButton>
                </Box>

                {/* Desktop Profile Dropdown Menu - only shown on md+ screens */}
                {!isMobile && (
                  <Menu
                    anchorEl={profileMenuAnchor}
                    open={Boolean(profileMenuAnchor)}
                    onClose={handleProfileMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiPaper-root': {
                        borderRadius: 2,
                        minWidth: 220,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        border: '1px solid #e5e7eb',
                        mt: 1
                      }
                    }}
                  >
                    {[
                      canViewProfile && { label: 'My Profile', icon: '/assets/img/website/icons/my_profile.svg', onClick: handleProfileClick },
                      showWhatsAppSmsAuth && { label: 'My Subscription', icon: '/assets/img/website/icons/my_subscription.svg', onClick: () => { handleProfileMenuClose(); navigate('/my-subscription'); } },
                      (permissionLoading || canViewBrands) && { label: 'Brand Logos', icon: '/assets/img/website/icons/brand_logos.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/brands'); } },
                      showWhatsAppSmsAuth && canViewAIModelStudio && { label: 'AI Model Studio', icon: '/assets/img/website/icons/ai_model_studio.svg', onClick: () => { handleProfileMenuClose(); navigate('/ai-model-studio'); }, gradient: true },
                      { label: 'Notifications', icon: '/assets/img/website/icons/notification.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/notifications'); } },
                      { label: 'Security', icon: '/assets/img/website/icons/security.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/security'); } },
                      !FlutterServicesAPI.isFlutterApp() && { label: 'Visit Website', icon: '/assets/img/website/icons/visit_web.svg', onClick: () => window.location.href = '/' },
                      { label: 'Sign Out', icon: '/assets/img/website/icons/sign_out.svg', onClick: handleLogoutClick, danger: true }
                    ].filter(Boolean).map((item, index) => (
                      <MenuItem
                        key={item.label}
                        onClick={item.onClick}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 1.3,
                          px: 2,
                          gap: 1.5,
                          '&:hover': {
                            bgcolor: item.danger ? '#fef2f2' : '#f9fafb'
                          }
                        }}
                      >
                        <Box component="img" src={item.icon} alt={item.label} sx={{ width: 22, height: 22, flexShrink: 0 }} />
                        {item.gradient ? (
                          <Box sx={{
                            background: 'linear-gradient(90deg, #A855F7 0%, #FCAF2B 100%)',
                            borderRadius: '6px',
                            px: 1.5,
                            py: 0.4
                          }}>
                            <Typography sx={{
                              fontFamily: 'var(--font-montserrat-medium)',
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#ffffff',
                              lineHeight: 1.2
                            }}>
                              {item.label}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{
                            fontFamily: 'var(--font-montserrat-medium)',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: item.danger ? '#dc2626' : '#141414'
                          }}>
                            {item.label}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                )}

                {/* Mobile Fullscreen Profile Drawer - full-width on mobile */}
                {isMobile && (
                  <Drawer
                    anchor="right"
                    open={Boolean(profileMenuAnchor)}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                      sx: {
                        width: '100%',
                        height: '100%',
                        bgcolor: '#F1F1F1'
                      }
                    }}
                  >
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Header with Logo and Avatar */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: '#ffffff', borderBottom: '1px solid #eee' }}>
                        <Box display="flex" alignItems="center" onClick={() => !FlutterServicesAPI.isFlutterApp() && navigate('/')} sx={{ cursor: 'pointer' }}>
                          <img
                            src="/assets/img/glammify-logo.png"
                            alt="Glammify Logo"
                            style={{ maxHeight: '30px' }}
                          />
                        </Box>
                        <IconButton
                          onClick={handleProfileMenuClose}
                          size="small"
                          sx={{
                            bgcolor: '#651BC6',
                            color: 'white',
                            width: 35,
                            height: 35,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              bgcolor: '#5115a3',
                            },
                          }}
                        >
                          <img
                            src="/assets/img/icons/close_icon.svg"
                            alt="Close"
                            style={{ width: 22, height: 22 }}
                          />
                        </IconButton>
                        {/* <Avatar
                          sx={{ bgcolor: '#a78bfa', width: 40, height: 40, cursor: 'pointer' }}
                          onClick={handleProfileMenuClose}
                        >
                          {merchant?.name ? merchant.name[0].toUpperCase() : (merchant?.email ? merchant.email[0].toUpperCase() : <Person />)}
                        </Avatar> */}
                      </Box>

                      {/* Menu Items Container */}
                      <Box sx={{ p: 2, flexGrow: 1 }}>
                        <Box sx={{ bgcolor: '#ffffff', borderRadius: '8px', overflow: 'hidden', p: 1 }}>
                          <List disablePadding>
                            {[
                              canViewProfile && { label: 'My Profile', icon: '/assets/img/website/icons/my_profile.svg', onClick: handleProfileClick },
                              showWhatsAppSmsAuth && { label: 'My Subscription', icon: '/assets/img/website/icons/my_subscription.svg', onClick: () => { handleProfileMenuClose(); navigate('/my-subscription'); } },
                              (permissionLoading || canViewBrands) && { label: 'Brand Logos', icon: '/assets/img/website/icons/brand_logos.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/brands'); } },
                              showWhatsAppSmsAuth && canViewAIModelStudio && { label: 'AI Model Studio', icon: '/assets/img/website/icons/ai_model_studio.svg', onClick: () => { handleProfileMenuClose(); navigate('/ai-model-studio'); }, gradient: true },
                              { label: 'Notifications', icon: '/assets/img/website/icons/notification.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/notifications'); } },
                              { label: 'Security', icon: '/assets/img/website/icons/security.svg', onClick: () => { handleProfileMenuClose(); navigate('/profile/security'); } },
                              !FlutterServicesAPI.isFlutterApp() && { label: 'Visit Website', icon: '/assets/img/website/icons/visit_web.svg', onClick: () => window.location.href = '/' },
                              { label: 'Sign Out', icon: '/assets/img/website/icons/sign_out.svg', onClick: handleLogoutClick }
                            ].filter(Boolean).map((item, index) => (
                              <MenuItem
                                key={item.label}
                                onClick={item.onClick}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  py: 1.3,
                                  px: 2,
                                  mt: index !== 0 ? 0.5 : 0,
                                  borderRadius: '6px',
                                  gap: 2,
                                  '&:hover': {
                                    bgcolor: '#f9fafb'
                                  }
                                }}
                              >
                                <Box component="img" src={item.icon} alt={item.label} sx={{ width: 26, height: 26 }} />

                                {item.gradient ? (
                                  <Box sx={{
                                    background: 'linear-gradient(90deg, #A855F7 0%, #FCAF2B 100%)',
                                    borderRadius: '6px',
                                    px: 1.5,
                                    py: 0.5
                                  }}>
                                    <Typography
                                      sx={{
                                        fontFamily: 'var(--font-montserrat-medium)',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: '#ffffff',
                                        lineHeight: 1.2
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontFamily: 'var(--font-montserrat-medium)',
                                      fontSize: '16px',
                                      fontWeight: 500,
                                      color: '#141414'
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </List>
                        </Box>
                      </Box>

                    </Box>
                  </Drawer>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Credit Balance Section - HIDDEN AS PER REQUEST
        {merchant && (location.pathname === '/dashboard' || location.pathname === '/generate' || location.pathname === '/gallery' || location.pathname === '/history') && (
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2, pb: 1, m: 0, mt: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                borderRadius: 3,
                px: 2,
                py: 1,
                color: 'black',
                gap: 2,
                border: '1px solid #e5e7eb',
                justifyContent: 'center' // Center the button since balance is hidden
              }}
            >
              <Box sx={{ display: 'none' }}>
                <MobileCreditBalance
                  credits={merchant?.credits_balance}
                  isUpdating={isUpdatingCredits}
                />
              </Box>
              {!FlutterServicesAPI.isFlutterApp() && (permissionLoading || canRequestTokens) && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/credits?tab=0')}
                  sx={{
                    background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: 14,
                    borderRadius: 2,
                    px: { xs: 1.5, sm: 2, md: 3 },
                    py: 1,
                    boxShadow: 'none',
                    textTransform: 'none',
                    width: '100%', // Make button wider on mobile
                    '&:hover': {
                      background: '#6318C6',
                      boxShadow: '0 4px 12px rgba(147, 51, 238, 0.3)',
                    },
                  }}
                >
                  <Add sx={{ fontSize: 14, mr: 0.5 }} />
                  Request {FlutterServicesAPI.getCreditLabel()}
                </Button>
              )}
            </Box>
          </Box>
        )}
      */}

      <Box
        className="merchant-layout-content"
        sx={{
          maxWidth: 1400,
          mx: 'auto',
          mt: { xs: isCreatePage ? 0 : 2, md: 2 },
          px: { xs: isCreatePage ? 0 : 2, md: 2 },
          pb: {
            xs: isCreatePage ? '0px' : '80px',
            md: '100px'
          }
        }}
      >
        <VerificationStatusBanner />
        {children}
      </Box>

      {/* Custom Credits Dialog - COMMENTED OUT: Now redirects to CreditsManagement page instead */}
      {/* 
      <Dialog open={customCreditsDialog} onClose={handleCancelCustomCredits} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Request Custom Credits
            <IconButton onClick={handleCancelCustomCredits} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Request a custom credit amount for your business needs. Minimum 100 credits, maximum {maxCreditLimit.toLocaleString()} credits.
          </Typography>
          <TextField
            fullWidth
            label="Credits Required *"
            type="number"
            value={customAmount}
            onChange={(e) => {
              const value = e.target.value;
              setCustomAmount(value);
              
              // Real-time validation
              const error = validateCreditAmount(value);
              setValidationError(error);
              setError(''); // Clear previous errors
            }}
            placeholder={`Enter credits between 100 and ${maxCreditLimit.toLocaleString()}`}
            inputProps={{ 
              min: 100, 
              max: maxCreditLimit 
            }}
            error={!!validationError}
            helperText={validationError || `Enter credits between 100 and ${maxCreditLimit.toLocaleString()}`}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Reason/Notes (Optional)"
            multiline
            rows={3}
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder="Please provide a reason for your custom credit request"
            helperText="Explain why you need custom credits (business purpose, project details, etc.)"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#6318C6',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '13px',
                opacity: 0.7
              }
            }}
          />
          
          {(validationError || error) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError || error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelCustomCredits}
            disabled={loading}
            sx={{
              textTransform: 'none',
              color: 'black'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomCredits}
            variant="contained"
            disabled={loading || !customAmount || !!validationError}
            sx={{
              background: loading || !customAmount || parseInt(customAmount) < 100 ? '#E0E0E0' : '#6318C6',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                background: loading || !customAmount || parseInt(customAmount) < 100 ? '#E0E0E0' : '#6318C6',
              },
              '&:disabled': {
                background: '#E0E0E0',
                color: '#424242'
              }
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      */}

      {/* Beautiful Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            color: '#374151', // Changed to dark color for better contrast on white
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
          <IconButton
            onClick={handleCloseSuccessDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#374151', // Changed to dark color
              '&:hover': { background: 'rgba(0,0,0,0.1)' }
            }}
          >
            <Close />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Box
              sx={{
                background: 'rgba(99, 24, 198, 0.1)', // Changed to light purple background
                borderRadius: '50%',
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#374151' }}>
              Success!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center', color: '#374151' }}>
              {successMessage}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            {requestDetails && (
              <Box sx={{ mt: 2, p: 2, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {requestDetails.packageName && (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <CreditCard sx={{ fontSize: 20 }} />
                      <Typography variant="body1" fontWeight="bold">
                        {requestDetails.packageName}
                      </Typography>
                    </Box>
                  )}
                  {requestDetails.credits && (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <AccountBalance sx={{ fontSize: 20 }} />
                      <Typography variant="body1">
                        {requestDetails.credits} {FlutterServicesAPI.getCreditLabel()}
                      </Typography>
                    </Box>
                  )}
                  {requestDetails.requestId && (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Assignment sx={{ fontSize: 20 }} />
                      <Typography variant="body2">
                        Request ID: {requestDetails.requestId}
                      </Typography>
                    </Box>
                  )}

                  {/* {requestDetails.status && (
                    <Chip
                      label={`Status: ${requestDetails.status}`}
                      sx={{
                        background: requestDetails.status === 'PENDING' ? '#FF9800' : '#4CAF50',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  )} */}

                  {requestDetails.instructions && (
                    <Box sx={{ mt: 2, p: 2, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        <Info sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        {requestDetails.instructions}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, color: '#374151' }}>
              You will be notified once your request is processed.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            size="large"
            sx={{
              background: '#6318C6',
              color: 'white !important',
              fontWeight: 'bold',
              minHeight: '50px',
              height: '50px',
              fontSize: '16px',
              px: 4,
              '&:hover': {
                background: '#5a16b0'
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat widget injected for all authenticated merchant pages */}

      <UsageLimitExceededModal
        open={!!limitExceeded}
        onClose={() => setLimitExceeded(null)}
        message={limitExceeded?.message}
        limit={limitExceeded?.limit}
        used={limitExceeded?.used}
        showWhatsAppSmsAuth={showWhatsAppSmsAuth}
      />

      <WhatsAppRequiredModal
        open={
          merchant !== null &&
          (!merchant?.whatsapp_number || !merchant?.whatsapp_code) &&
          location.pathname !== '/profile'
        }
      />
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only re-render if children prop actually changes
  return prevProps.children === nextProps.children;
});

export default MerchantLayout; 